/**
 * Two to four mutually exclusive options in one inset track — "ALL · AND |
 * ANY · OR", "Dynamic | Static", "7 days | 30 days". The concept's `.sb-join`,
 * `.sb-mode` and the flow editor's `.fe-modes`, which were four skins of one
 * control.
 *
 * ## WHY NOT TABS, AND WHY NOT A RADIO GROUP
 *
 * Tabs are for panels: each tab owns a region and `aria-controls` points at
 * it. This toggles a *value* that the screen around it reads — a chart window,
 * a join — and there is no panel to point at, so a tablist would announce a
 * structure that is not there. It is a `role="group"` of `aria-pressed`
 * buttons, exactly one pressed, for the same reasons `ScopeChips` is: one
 * keyboard model across the product (Tab, then Space or Enter), and no
 * re-render of a whole chart on every arrow key.
 *
 * ## THE COLOURS AND THE SHAPE
 *
 * The track is `bg-black/30` inside a `line-strong` hairline. The pressed fill
 * is the caller's `tone`: `brand` is `brand-a/32` with white text (12.86:1),
 * `neutral` is a white overlay, and an identity tone is that colour's /24 wash
 * — the phase-B "ANY" group, which the concept drew in warn's amber and this
 * product draws in apricot, because "any of these" is not a warning.
 * Unpressed text is muted, not the faint token the concept used. Radii follow
 * the product scale: the concept's 8 and 6 become `rounded-sm` and
 * `rounded-xs`.
 *
 * The pressed state is not carried by colour alone. Against the track the
 * brand fill measures 1.41:1 and the neutral one 1.17:1, and under forced
 * colours both are replaced by system colours. So the pressed option is also
 * `font-medium`, takes an underline under `forced-colors` (as `Tabs` does),
 * and the neutral tone adds an inset `line-strong` ring (slice 66 review).
 *
 * Both sizes share one type role — the mono, tracked join label — and differ
 * only in padding. The first build switched to sans at `md`, and the Overview
 * window toggle and the template preview's width toggle read as two different
 * components.
 *
 * `sub` is a second, smaller word inside the option — the "AND" under "ALL".
 * It is not dimmed with opacity, which would drop muted text below 4.5:1; it
 * is smaller and takes the option's colour.
 *
 * ## THE HOLE IT LEAVES
 *
 * It does not stop a caller passing one option, or ten. Two to four is the
 * range in which a segmented track reads at a glance; past that, use a Select.
 */
import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import type { IdentTone } from '../lib/identity';

const PRESSED = {
  brand: 'bg-brand-a/32 text-white',
  neutral: 'bg-white/[0.08] text-text ring-1 ring-inset ring-line-strong',
  'ident-a': 'bg-ident-a/24 text-text',
  'ident-b': 'bg-ident-b/24 text-text',
  'ident-c': 'bg-ident-c/24 text-text',
  'ident-d': 'bg-ident-d/24 text-text',
} as const satisfies Readonly<Record<'brand' | 'neutral' | IdentTone, string>>;

export type SegmentedToggleTone = keyof typeof PRESSED;

/** The one type role both sizes share: the concept's `.sb-join` label. */
const TYPE_ROLE = 'font-mono text-[0.68rem] uppercase tracking-[0.08em]';

/** Sizes differ in padding only, so a small and a large toggle read as one control. */
const SIZE = {
  sm: 'px-[9px] py-[3px]',
  md: 'px-3 py-1',
} as const;

export type SegmentedToggleSize = keyof typeof SIZE;

export interface SegmentedToggleOption {
  readonly value: string;
  readonly label: string;
  /** A smaller second word inside the option — "AND" under "ALL". */
  readonly sub?: string;
}

export interface SegmentedToggleProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  /** The group's accessible name — "Chart window". */
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: readonly SegmentedToggleOption[];
  /** The pressed fill. Default `'brand'`. */
  tone?: SegmentedToggleTone;
  /** Default `'md'`. Padding only; both sizes share the mono join label. */
  size?: SegmentedToggleSize;
}

export function SegmentedToggle({
  label,
  value,
  onValueChange,
  options,
  tone = 'brand',
  size = 'md',
  className,
  ...rest
}: SegmentedToggleProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('inline-flex rounded-sm border border-line-strong bg-black/30 p-0.5', className)}
      {...rest}
    >
      {options.map((option) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'inline-flex min-h-6 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-xs',
              'transition-colors duration-150',
              TYPE_ROLE,
              SIZE[size],
              // After the type role, not before: tailwind-merge drops a
              // leading class that a font-size class follows in the same call.
              'leading-none',
              pressed ? cn(PRESSED[tone], 'font-medium forced-colors:underline') : 'text-muted hover:text-text',
              focusRing,
            )}
          >
            <span>{option.label}</span>
            {option.sub !== undefined && <small className="text-[0.6rem] leading-none">{option.sub}</small>}
          </button>
        );
      })}
    </div>
  );
}
